import { memo, useCallback } from 'react';
import type { GestureResponderEvent, StyleProp, TextStyle } from 'react-native';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Checkbox, TouchableRipple } from 'react-native-paper';
import Animated, { useAnimatedStyle, withSequence, withTiming } from 'react-native-reanimated';

import { rippleOnLink, rippleOnSurface } from '@/theme';

export interface ContactSelectionCardProps {
  readonly title: string;
  readonly subtitle: string;
  readonly subtitleColor: string | null;
  readonly notes: string | null | undefined;
  readonly lastActionText: string | null | undefined;
  readonly selected: boolean;
  readonly disabled?: boolean;
  readonly onToggle: () => void;
  readonly onNotesPress?: () => void;
  readonly onViewProfile?: () => void;
  readonly seeMemoLabel?: string;
  readonly viewProfileLabel?: string;
}

interface ContentPreviewProps {
  readonly notes: string | null | undefined;
  readonly lastActionText: string | null | undefined;
}

interface FooterLinksProps {
  readonly hasNotes: boolean;
  readonly onNotesPress?: () => void;
  readonly onViewProfile?: () => void;
  readonly seeMemoLabel?: string;
  readonly viewProfileLabel?: string;
}

const CHECKBOX_SELECTED_COLOR = '#82c91e';
const CHECKBOX_UNSELECTED_COLOR = '#527187';

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    borderColor: '#d5dfe6',
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    paddingBottom: 16,
    paddingHorizontal: 16,
    paddingTop: 16,
    width: '100%',
  },
  containerDisabled: {
    opacity: 0.5,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
  },
  headerContent: {
    flex: 1,
    gap: 4,
  },
  title: {
    color: '#17344a',
    fontSize: 16,
    fontWeight: '400',
  },
  subtitle: {
    color: '#254157',
    fontSize: 12,
  },
  checkbox: {
    margin: 0,
  },
  content: {
    marginTop: 12,
  },
  notes: {
    color: '#17344a',
    fontSize: 12,
    lineHeight: 18,
  },
  lastAction: {
    color: '#254157',
    fontSize: 12,
    lineHeight: 18,
  },
  footer: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 12,
  },
  link: {
    color: '#1864ab',
    fontSize: 14,
    fontWeight: '500',
  },
  linkTouchable: {
    borderRadius: 4,
    overflow: 'hidden',
  },
});

const AnimatedTouchableRipple = Animated.createAnimatedComponent(TouchableRipple);

const stopEvent = (event?: GestureResponderEvent) => {
  event?.stopPropagation?.();
};

const getSubtitleStyle = (subtitleColor: string | null): StyleProp<TextStyle> => [
  styles.subtitle,
  subtitleColor ? { color: subtitleColor } : null,
];

const ContentPreview = ({ notes, lastActionText }: ContentPreviewProps) => {
  if (notes) {
    return (
      <View style={styles.content}>
        <Text numberOfLines={2} ellipsizeMode="tail" style={styles.notes}>
          {notes}
        </Text>
      </View>
    );
  }

  if (!lastActionText) {
    return null;
  }

  return (
    <View style={styles.content}>
      <Text style={styles.lastAction}>{lastActionText}</Text>
    </View>
  );
};

const FooterLinks = ({
  hasNotes,
  onNotesPress,
  onViewProfile,
  seeMemoLabel,
  viewProfileLabel,
}: FooterLinksProps) => {
  const handleNotesPress = useCallback(
    (event: GestureResponderEvent) => {
      stopEvent(event);
      onNotesPress?.();
    },
    [onNotesPress],
  );

  const handleViewProfilePress = useCallback(
    (event: GestureResponderEvent) => {
      stopEvent(event);
      onViewProfile?.();
    },
    [onViewProfile],
  );

  const claimResponder = useCallback(() => true, []);

  return (
    <View style={styles.footer} onStartShouldSetResponder={claimResponder}>
      {hasNotes ? (
        <TouchableRipple
          disabled={!onNotesPress}
          onPress={handleNotesPress}
          rippleColor={rippleOnLink}
          style={styles.linkTouchable}
          testID="contact-selection-card-see-memo">
          <Text style={styles.link}>{seeMemoLabel ?? 'See memo'}</Text>
        </TouchableRipple>
      ) : null}
      <TouchableRipple
        disabled={!onViewProfile}
        onPress={handleViewProfilePress}
        rippleColor={rippleOnLink}
        style={styles.linkTouchable}
        testID="contact-selection-card-view-profile">
        <Text style={styles.link}>{viewProfileLabel ?? 'View profile'}</Text>
      </TouchableRipple>
    </View>
  );
};

const ContactSelectionCardBase = ({
  title,
  subtitle,
  subtitleColor,
  notes,
  lastActionText,
  selected,
  disabled,
  onToggle,
  onNotesPress,
  onViewProfile,
  seeMemoLabel,
  viewProfileLabel,
}: ContactSelectionCardProps) => {
  const handleCheckboxPress = useCallback(
    (event: GestureResponderEvent) => {
      stopEvent(event);
      onToggle();
    },
    [onToggle],
  );

  const animatedContainerStyle = useAnimatedStyle(
    () => ({
      borderColor: withTiming(selected ? CHECKBOX_SELECTED_COLOR : '#d5dfe6', { duration: 200 }),
    }),
    [selected],
  );

  const animatedCheckboxStyle = useAnimatedStyle(
    () => ({
      transform: [
        {
          scale: selected
            ? withSequence(withTiming(1.08, { duration: 100 }), withTiming(1, { duration: 100 }))
            : withTiming(1, { duration: 100 }),
        },
      ],
    }),
    [selected],
  );

  return (
    <AnimatedTouchableRipple
      onPress={onToggle}
      disabled={disabled}
      rippleColor={rippleOnSurface}
      style={[styles.container, disabled ? styles.containerDisabled : null, animatedContainerStyle]}
      testID="contact-selection-card-pressable">
      <View>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <Text style={styles.title}>{title}</Text>
            <Text style={getSubtitleStyle(subtitleColor)}>{subtitle}</Text>
          </View>
          <Pressable
            onPress={handleCheckboxPress}
            disabled={disabled}
            accessibilityRole="checkbox"
            accessibilityState={
              disabled ? { checked: selected, disabled: true } : { checked: selected }
            }
            testID="contact-selection-card-checkbox">
            <Animated.View style={animatedCheckboxStyle}>
              <Checkbox.Android
                color={CHECKBOX_SELECTED_COLOR}
                status={selected ? 'checked' : 'unchecked'}
                uncheckedColor={CHECKBOX_UNSELECTED_COLOR}
                disabled={disabled}
                style={styles.checkbox}
              />
            </Animated.View>
          </Pressable>
        </View>
        <ContentPreview notes={notes} lastActionText={lastActionText} />
        <FooterLinks
          hasNotes={Boolean(notes)}
          onNotesPress={onNotesPress}
          onViewProfile={onViewProfile}
          seeMemoLabel={seeMemoLabel}
          viewProfileLabel={viewProfileLabel}
        />
      </View>
    </AnimatedTouchableRipple>
  );
};

export const ContactSelectionCard = memo(ContactSelectionCardBase);
